import React from 'react';
import {
  Card, CardContent, Avatar, Typography, Chip, Box, Stack,
  Tooltip, LinearProgress, useTheme, alpha
} from '@mui/material';
import {
  EmojiEvents, Star, TrendingUp, LocalFireDepartment,
  EmojiNature, WorkspacePremium
} from '@mui/icons-material';

const LeaderboardCard = ({
  entry,
  rank,
  showDetailedStats = false,
  isCurrentUser = false,
  animated = true
}) => {
  const theme = useTheme();

  // Dynamic rank styling
  const getRankStyle = (position) => {
    if (position === 1) return {
      gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
      icon: '🥇',
      shadow: '0 8px 32px rgba(255, 215, 0, 0.4)',
      border: '3px solid #FFD700'
    };
    if (position === 2) return {
      gradient: 'linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%)',
      icon: '🥈',
      shadow: '0 6px 24px rgba(192, 192, 192, 0.4)',
      border: '2px solid #C0C0C0'
    };
    if (position === 3) return {
      gradient: 'linear-gradient(135deg, #CD7F32 0%, #8D6E63 100%)',
      icon: '🥉',
      shadow: '0 4px 20px rgba(205, 127, 50, 0.4)',
      border: '2px solid #CD7F32'
    };
    if (position <= 10) return {
      gradient: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
      icon: '⭐',
      shadow: '0 4px 16px rgba(67, 160, 71, 0.3)',
      border: '1px solid #43a047'
    };
    return {
      gradient: 'linear-gradient(135deg, #81c784 0%, #a5d6a7 100%)',
      icon: '🌱',
      shadow: '0 2px 12px rgba(129, 199, 132, 0.2)',
      border: '1px solid #81c784'
    };
  };

  // Badge icon mapping
  const getBadgeIcon = (category, name) => {
    const specificIcons = {
      'Green Starter': '🌱',
      'Marathoner': '🏃‍♂️',
      'Weekly Warrior': '⚔️',
      'Sharpshooter': '🎯',
      'Correctionist': '✨',
    };

    if (specificIcons[name]) return specificIcons[name];

    const categoryIcons = {
      'ACHIEVEMENT': '🏆',
      'MILESTONE': '🎖️',
      'STREAK': '🔥',
      'ACCURACY': '🎯',
      'SPECIAL': '✨',
    };
    return categoryIcons[category] || '🏅';
  };

  // Level styling
  const getLevelStyle = (level) => {
    const styles = {
      'Rookie': { bg: '#e8f5e8', color: '#2e7d32', icon: '🌱' },
      'Apprentice': { bg: '#c8e6c9', color: '#1b5e20', icon: '⚔️' },
      'Sorter': { bg: '#4caf50', color: '#ffffff', icon: '♻️' },
      'Pro Sorter': { bg: '#43a047', color: '#ffffff', icon: '🌍' },
      'Master Sorter': { bg: '#2e7d32', color: '#ffffff', icon: '👑' },
      'Eco-Legend': { bg: '#1b5e20', color: '#ffffff', icon: '🏆' },
    };
    return styles[level] || styles['Rookie'];
  };

  const rankStyle = getRankStyle(rank);
  const levelStyle = getLevelStyle(entry.currentLevel);

  return (
    <Card
      elevation={rank <= 3 ? 12 : 6}
      sx={{
        background: isCurrentUser
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
          : 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)',
        border: isCurrentUser ? `3px solid ${theme.palette.primary.main}` : rankStyle.border,
        borderRadius: 4,
        overflow: 'hidden',
        transition: animated ? 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' : 'none',
        transform: animated ? 'translateY(0px)' : 'none',
        boxShadow: rankStyle.shadow,
        position: 'relative',
        '&:hover': animated ? {
          transform: 'translateY(-2px) scale(1.005)', // Reduced hover effect
          boxShadow: `${rankStyle.shadow}, 0 8px 24px rgba(0,0,0,0.06)`,
          '& .rank-badge': {
            transform: 'rotate(360deg) scale(1.05)',
          }
        } : {},
        '&::before': rank <= 3 ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px', // Reduced from 3px
          background: rankStyle.gradient,
          zIndex: 1
        } : {},
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2.5, position: 'relative' }}> {/* Much more compact padding */}
        <Stack direction="row" spacing={1.5} alignItems="center"> {/* Reduced spacing */}
          {/* Rank Badge - Much Smaller */}
          <Box sx={{ position: 'relative', minWidth: 50, textAlign: 'center' }}> {/* Reduced size */}
            <Box
              className="rank-badge"
              sx={{
                width: 40, // Reduced from 50
                height: 40, // Reduced from 50
                borderRadius: '50%',
                background: rankStyle.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: rankStyle.shadow,
                transition: 'all 0.3s ease',
                mb: 0.3 // Reduced margin
              }}
            >
              <Typography variant="h5" sx={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}> {/* Much smaller icon */}
                {rankStyle.icon}
              </Typography>
            </Box>
            <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              #{rank}
            </Typography>
          </Box>

          {/* Avatar - Much Smaller */}
          <Avatar
            src={entry.profilePhotoUrl}
            alt={entry.displayName}
            sx={{
              width: 45, // Reduced from 60
              height: 45, // Reduced from 60
              border: `2px solid ${rankStyle.gradient.includes('FFD700') ? '#FFD700' : theme.palette.primary.main}`,
              boxShadow: '0 3px 12px rgba(0,0,0,0.1)',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}
          >
            {entry.displayName?.charAt(0) || '?'}
          </Avatar>

          {/* User Info - Much More Condensed */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.3 }}>
              <Typography
                variant="subtitle1" // Much smaller
                fontWeight={600} // Reduced from 700
                sx={{
                  color: theme.palette.text.primary,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  fontSize: '0.95rem'
                }}
              >
                {entry.displayName || 'Anonymous Eco Warrior'}
              </Typography>
              {isCurrentUser && (
                <Chip
                  label="You"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                />
              )}
            </Stack>

            {/* Stats Row - Very Compact */}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Chip
                icon={<span style={{ fontSize: '12px' }}>{levelStyle.icon}</span>}
                label={entry.currentLevel || 'Rookie'}
                size="small"
                sx={{
                  background: levelStyle.bg,
                  color: levelStyle.color,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                  '& .MuiChip-icon': { color: levelStyle.color }
                }}
              />

              <Chip
                icon={<span style={{ fontSize: '12px' }}>♻️</span>}
                label={`${entry.classificationsCount || 0}`}
                size="small"
                sx={{
                  background: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.dark,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20
                }}
              />

              {/* STREAK - More Prominent */}
              {entry.currentStreak > 0 && (
                <Chip
                  icon={<LocalFireDepartment sx={{ fontSize: '12px !important' }} />}
                  label={`${entry.currentStreak}🔥`}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #ff5722 0%, #ff8a65 100%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 20,
                    boxShadow: '0 2px 6px rgba(255, 87, 34, 0.3)',
                    border: '1px solid rgba(255, 87, 34, 0.2)'
                  }}
                />
              )}
            </Stack>

            {/* Badges - Very Compact */}
            {entry.badges && entry.badges.length > 0 && (
              <Stack direction="row" spacing={0.3} flexWrap="wrap">
                {entry.badges.slice(0, 2).map((badge, index) => ( // Show only 2 badges
                  <Tooltip
                    key={`${badge.name}-${index}`}
                    title={`${getBadgeIcon(badge.category, badge.name)} ${badge.name}`}
                  >
                    <Chip
                      icon={<span style={{ fontSize: '10px' }}>{getBadgeIcon(badge.category, badge.name)}</span>}
                      label={badge.name.split(' ')[0]} // Show only first word
                      size="small"
                      sx={{
                        background: badge.rarity === 'LEGENDARY' ? 'linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)' :
                                   badge.rarity === 'EPIC' ? 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)' :
                                   badge.rarity === 'RARE' ? 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)' :
                                   'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.55rem',
                        height: 16,
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </Tooltip>
                ))}
                {entry.badges.length > 2 && (
                  <Chip
                    label={`+${entry.badges.length - 2}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.55rem', height: 16 }}
                  />
                )}
              </Stack>
            )}
          </Box>

          {/* Points Display - Compact but prominent */}
          <Box textAlign="center" sx={{ minWidth: 70 }}>
            <Typography
              variant="h5" // Reduced from h4
              fontWeight={700} // Reduced from 800
              sx={{
                color: theme.palette.primary.main,
                textShadow: `0 1px 4px ${alpha(theme.palette.primary.main, 0.3)}`,
                mb: 0.3,
                lineHeight: 1,
                fontSize: '1.3rem'
              }}
            >
              {(entry.rewardsPoints || entry.totalPoints || entry.weeklyPoints || entry.monthlyPoints || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
              Points
            </Typography>

            {/* Progress bar - Only if authenticated and has progress */}
            {showDetailedStats && entry.nextLevelProgress !== undefined && (
              <Box sx={{ mt: 0.3, width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={entry.nextLevelProgress}
                  sx={{
                    height: 4, // Reduced from default
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: 2
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  {Math.round(entry.nextLevelProgress * 10) / 10}% progress
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LeaderboardCard;
